// Lightweight role detection from a profile's one-liner / bio. Used to
// adapt example copy across the editor (Q&A example, chat prompt chips,
// onboarding hints) so a realtor doesn't see "What camera do you use?
// Sony A7SIII" when they're trying to set up a real-estate AI.
//
// Keyword-match implementation. Cheap, no API calls. Misses edge cases —
// a "real estate photographer" is a creator first by intent here. That's
// OK; the user can override examples manually. The goal isn't perfect
// classification, it's not feeling like the product was built for
// someone else.

const ROLE_KEYWORDS = [
  ['realtor',  ['realtor', 'real estate', 'real-estate', 'broker', 'realty', 'mls', 'home buyer', 'home seller', 'agent of properties']],
  ['legal',    ['lawyer', 'attorney', 'legal counsel', 'paralegal', 'esq.', 'law firm']],
  ['medical',  ['doctor', 'physician', 'dentist', 'orthodontist', 'chiropractor', 'therapist', 'psychiatrist', 'medical practice', 'clinic owner']],
  ['service',  ['plumber', 'plumbing', 'hvac', 'cleaning service', 'painting', 'electrician', 'roofing', 'contractor', 'handyman', 'landscaper']],
  ['coach',    ['coach', 'consultant', 'fitness trainer', 'personal trainer', 'business coach', 'life coach']],
  ['educator', ['teacher', 'professor', 'tutor', 'instructor', 'educator', 'curriculum', 'classroom']],
  ['founder',  ['founder', 'ceo', 'co-founder', 'startup', 'entrepreneur']],
  ['creator',  ['creator', 'designer', 'artist', 'photographer', 'videographer', 'youtuber', 'influencer', 'streamer', 'producer', 'illustrator', 'storyteller']],
];

export function detectRole(profileData) {
  if (!profileData) return 'creator';
  const haystack = [
    profileData.tagline,
    profileData.profession,
    profileData.role,
    profileData.bio,
    profileData.userGoal,
  ].filter(Boolean).join(' ').toLowerCase();
  if (!haystack) return 'creator';
  for (const [role, keywords] of ROLE_KEYWORDS) {
    if (keywords.some(kw => haystack.includes(kw))) return role;
  }
  return 'creator';
}

// Q&A example shown in the AI Brain empty state. Picks a question +
// answer pair that matches the user's role, so a realtor sees real
// estate examples and a creator sees creator examples.
const ROLE_QNA_EXAMPLES = {
  realtor:  { q: 'What neighborhoods do you specialize in?', a: 'Cherry Creek, Wash Park, and Highlands. All walkable, tree-lined, close to downtown.' },
  legal:    { q: 'Do you offer free consultations?', a: 'First 30 minutes are free. After that, I bill at $300/hour or take cases on contingency depending on the matter.' },
  medical:  { q: 'Do you accept new patients?', a: 'Yes — currently accepting new patients. We take most major insurance plans, plus self-pay.' },
  service:  { q: 'What areas do you service?', a: 'Greater Denver Metro and Boulder. Same-day service available within a 20-mile radius.' },
  coach:    { q: 'How does a typical session work?', a: '60 minutes over Zoom or in person. We start with where you are, set the next step, and I check in twice between sessions.' },
  educator: { q: 'Do you tutor online or in person?', a: 'Both — online via Zoom for flexibility, in person within 15 miles for hands-on subjects.' },
  founder:  { q: 'What stage do you invest in?', a: 'Pre-seed and seed. Check sizes from $25K to $250K. I lean software and consumer.' },
  creator:  { q: 'What camera do you use?', a: 'Sony A7SIII for video, Fuji X100V for street photography. Happy to share my full kit list.' },
};

export function getQnaExample(profileData) {
  const role = detectRole(profileData);
  return ROLE_QNA_EXAMPLES[role] || ROLE_QNA_EXAMPLES.creator;
}

// Default chat-prompt chips shown above the chat input when a visitor
// opens the chat. Three quick-tap topics that should match what the
// profile is about. Realtor visitors should see "Home buying / Schools
// / Pricing" not "Setup / Travel / Life advice."
const ROLE_CHAT_PROMPTS = {
  realtor:  ['🏠 Home buying?', '🏘️ Neighborhoods?', '💰 Pricing?'],
  legal:    ['⚖️ Case types?', '📅 Consultation?', '💵 Fees?'],
  medical:  ['🩺 New patients?', '📅 Booking?', '🏥 Insurance?'],
  service:  ['📍 Service area?', '⏰ Hours?', '💵 Pricing?'],
  coach:    ['🎯 Sessions?', '📅 Availability?', '💵 Pricing?'],
  educator: ['🎓 Subjects?', '📅 Schedule?', '🌐 Online or in person?'],
  founder:  ['💼 Investing?', '📈 Stage?', '🤝 Partnerships?'],
  creator:  ['📸 Setup?', '✈️ Travel?', '☕ Life advice?'],
};

export function getDefaultChatPrompts(profileData) {
  const role = detectRole(profileData);
  return ROLE_CHAT_PROMPTS[role] || ROLE_CHAT_PROMPTS.creator;
}
